import type { SanityClient } from '@sanity/client'

import { callApiFromServerSide } from '~/services/axiosInstance.service'
import {
  CurriculumCompetencyDataType,
  CurriculumGradeDataType,
  CurriculumTopicDataType,
  GradeOverviewFromSanity,
  QuartarDataType,
  SanityGradeOverviewResponse,
  SanityGradeOverviewSubjectOrder,
  SubtopicContent,
} from '~/curriculum/types/curriculum.types'
import { SchoolClassGradeOption } from '~/school-admin/types/school-admin.types'
import { CurriculumCountryCodes } from '~/curriculum/constants/curriculum.const'
import { GradeType } from '~/onboarding/utils/grade-color.utils'
import { TopicId } from '~/dashboard/utils/learningPath.utils'

import sanityClient from './sanity-client'
import {
  curriculumActivitiesGroq,
  curriculumGradeOverview,
  curriculumGradesList,
  getTopicInfoForMappingQuiz,
  curriculumStandaloneContent,
  curriculumSubtopicTitles,
  curriculumSubtopicList,
  getSubjectInfoForMappingQuiz,
  curriculumGradesListV1,
  curriculumGradeTitles,
  curriculumGradeLabels,
  schoolClassGradeOptions,
} from './groq/curriculum.groq'
import { getCurriculumRelevantCountryCodeAndGradeId } from './utils/curriculum.utils'

// Shape of curriculumSubtopicTitles: subdivisions are unfiltered, so every one comes back
// and only the subdivision holding the matching subjectOrder has a non-empty topics array.
// `contents` is filtered down to the single doc matching $slug, so it holds 0 or 1 entry.
interface SanitySubtopicTitlesResponse {
  gradeLevel?: number
  subDivisions?: Array<{
    topics?: Array<{
      competencyGoals?: Array<{
        description?: string
        contents?: Array<{ title?: string; learningMaterials?: Array<{ isArticle?: boolean }> }>
      }>
    }>
  }>
}

interface CurriculumContentResponseItem {
  id: number
  countryCode: string
  gradeLevel: number
  gradeId: string
  subjectOrder: number
  subjectLabel: string
  subjectIds: string[]
  gradeLabel: string
  subdivisionOrder: number
  subdivisionLabel: string
  subjectContent: {
    subjectLabel: string
    topicId: TopicId
    competencyGoals: CurriculumCompetencyDataType[]
    subjectOrder: number
    subjectOrderLabel: string
    icon?: string
  }
}

class CurriculumSanity {
  private client: SanityClient
  public constructor() {
    this.client = sanityClient
  }

  public async getCurriculumGradeListByCountry(curriculumCountryCode: string, version = 'v2'): Promise<GradeType[]> {
    const countryCode =
      curriculumCountryCode === CurriculumCountryCodes.UKRAINIAN
        ? CurriculumCountryCodes.DEFAULT
        : curriculumCountryCode
    let groqCountry = countryCode
    if (countryCode === CurriculumCountryCodes.DEFAULT) {
      groqCountry = 'us'
    }

    const groqResponse = await this.client.fetch(version === 'v1' ? curriculumGradesListV1 : curriculumGradesList, {
      countryCode: groqCountry,
    })

    return groqResponse
  }

  /**
   * Grades available for one curriculum region, for the school admin class form.
   * `gradeLevel` is the numeric level; `gradeId` is the Sanity document id.
   */
  public async getSchoolClassGradeOptions(countryCode: string): Promise<SchoolClassGradeOption[]> {
    const groqResponse: SchoolClassGradeOption[] = await this.client.fetch(schoolClassGradeOptions, { countryCode })

    return groqResponse ?? []
  }

  // Resolves multiple gradeIds to their titles in a single query; returns a gradeId -> title map.
  public async getCurriculumGradeTitles(gradeIds: string[]): Promise<Record<string, string>> {
    if (!gradeIds.length) return {}
    const groqResponse: Array<{ id: string; title: string | null }> = await this.client.fetch(curriculumGradeTitles, {
      ids: gradeIds,
    })
    return (groqResponse ?? []).reduce<Record<string, string>>(
      (acc, { id, title }) => (title ? { ...acc, [id]: title } : acc),
      {},
    )
  }
  // Resolves one curriculum to its grade labels; returns a `${countryCode}-${gradeLevel}` -> label map.
  public async getCurriculumGradeLabels(countryCode: string): Promise<Record<string, string>> {
    const groqResponse: Array<{ gradeLevel: number; gradeLabel: string | null }> = await this.client.fetch(
      curriculumGradeLabels,
      { countryCode },
    )

    return (groqResponse ?? []).reduce<Record<string, string>>(
      (acc, { gradeLevel, gradeLabel }) =>
        gradeLabel ? { ...acc, [`${countryCode}-${gradeLevel}`]: gradeLabel } : acc,
      {},
    )
  }

  /**
   * Title for a curriculum link: the content doc matching `slug`, falling back to the subtopic's
   * competency-goal description when the slug is missing or isn't one of its contents (e.g. a
   * must-know level). The matching subjectOrder sits in exactly one subdivision, so search all of
   * them. `isArticle` covers the matched content only — false for the description fallback.
   */
  public async getCurriculumPreviewTitle({
    id,
    order,
    subtopic,
    slug,
  }: {
    id: string
    order: number
    subtopic: number
    slug?: string
  }): Promise<{ title: string; isArticle: boolean; gradeLevel?: number } | null> {
    const groqResponse: SanitySubtopicTitlesResponse = await this.client.fetch(curriculumSubtopicTitles, {
      id,
      order,
      subtopic,
      slug: slug ?? '',
    })

    const competencyGoals = (groqResponse?.subDivisions ?? [])
      .flatMap((subdivision) => subdivision?.topics ?? [])
      .flatMap((topic) => topic?.competencyGoals ?? [])

    const content = competencyGoals.flatMap((goal) => goal?.contents ?? []).find((item) => item?.title)
    const title = content?.title ?? competencyGoals.map((goal) => goal?.description).find(Boolean)
    const learningMaterials = content?.learningMaterials ?? []
    const isArticle = learningMaterials.length > 0 && learningMaterials.every((material) => !!material?.isArticle)

    return title ? { title, isArticle, gradeLevel: groqResponse?.gradeLevel } : null
  }

  public async getCurriculumSubtopicList({
    id,
    order,
    subtopic,
  }: {
    id: string
    order: number
    subtopic: number
  }): Promise<GradeOverviewFromSanity> {
    const groqResponse: SanityGradeOverviewResponse = await this.client.fetch(curriculumSubtopicList, {
      id,
      order,
      subtopic,
    })
    const doc = groqResponse?.[0]
    if (!doc) {
      return { gradeId: id, gradeLevel: 0, title: '', gradeLabel: '', subDivisions: [] }
    }

    const subDivisions = doc.gradeSubdivisions.map((subdivision) => ({
      _id: subdivision._id,
      label: subdivision.label,
      order: subdivision.level,
      topics: this.mapSanityGradeOverviewToTopics(subdivision.subjectOrders),
    }))

    return {
      gradeId: doc._id,
      gradeLevel: doc.gradeLevel,
      title: doc.title ?? '',
      gradeLabel: doc.title ?? '',
      subDivisions: subDivisions.toSorted((a, b) => a.order - b.order),
    }
  }

  // XXX: this call to sanity takes more than 7 seconds to complete, in terms of UI, it's not acceptable.
  // To improve the performance, we replaced it with a call to the hom-api that pre-store the curriculum content in the database.
  // This is a temporary solution to improve the performance of the frontend.
  public async getCurriculumContentList(
    gradeId: string,
    curriculumCountryCode: string,
  ): Promise<CurriculumGradeDataType> {
    const curriculumGradeDataResponse = await callApiFromServerSide<CurriculumContentResponseItem[]>({
      url: '/v1/curriculum/content',
      method: 'GET',
      params: getCurriculumRelevantCountryCodeAndGradeId(curriculumCountryCode, gradeId),
    })

    const responseData = curriculumGradeDataResponse?.data ?? []

    if (!responseData.length) {
      return {
        gradeLevel: 0,
        gradeLabel: '',
        title: '',
        isJuniorMathGrade: false,
        hasSubdivision: false,
        gradeSubdivisions: [],
        progressIds: [],
      }
    }

    const firstItem = responseData[0]
    const hasSubdivision = responseData[0].subdivisionOrder > 0
    // Group by subdivisionOrder using reduce
    const subdivisionsMap = responseData.reduce((acc, item) => {
      const existing = acc[item.subdivisionOrder]
      return {
        ...acc,
        [item.subdivisionOrder]: {
          subdivisionLabel: existing?.subdivisionLabel ?? item.subdivisionLabel ?? '',
          items: [...(existing?.items ?? []), item],
        },
      }
    }, {} as Record<number, { subdivisionLabel: string; items: CurriculumContentResponseItem[] }>)

    // Build gradeSubdivisions
    const gradeSubdivisions: QuartarDataType[] = Object.entries(subdivisionsMap)
      .map(([subdivisionOrderStr, data]) => {
        const subdivisionOrder = Number(subdivisionOrderStr)

        // Group by subjectOrder within this subdivision (deduplicate by keeping first occurrence)
        const subjectsMap = data.items.reduce((acc, item) => {
          if (acc[item.subjectOrder]) return acc
          return { ...acc, [item.subjectOrder]: item }
        }, {} as Record<number, CurriculumContentResponseItem>)

        const subjects: CurriculumTopicDataType[] = Object.values(subjectsMap)
          .map((item) => ({
            topicId: item.subjectContent.topicId,
            subjectLabel: item.subjectContent.subjectLabel,
            subjectOrder: item.subjectContent.subjectOrder,
            subjectOrderLabel: item.subjectContent.subjectOrderLabel,
            icon: item.subjectContent.icon,
            isFirstVisit: false,
            competencyGoals: item.subjectContent.competencyGoals,
          }))
          .toSorted((a, b) => a.subjectOrder - b.subjectOrder)

        return {
          subdivisionLabel: data.subdivisionLabel,
          subdivisionOrder,
          subjects,
        }
      })
      .toSorted((a, b) => a.subdivisionOrder - b.subdivisionOrder)

    const curriculumGradeData: CurriculumGradeDataType = {
      gradeLevel: firstItem.gradeLevel,
      gradeLabel: firstItem.gradeLabel,
      title: firstItem.gradeLabel,
      isJuniorMathGrade: false,
      hasSubdivision,
      gradeSubdivisions,
      progressIds: [],
    }

    return curriculumGradeData
  }

  public async getCurriculumActivities(_id: string): Promise<any[]> {
    const groqResponse = await this.client.fetch(curriculumActivitiesGroq, {
      _id,
    })

    return groqResponse
  }

  // Fetches a curriculumContent doc by id alone, with no grade/topic/subtopic context.
  public async getStandaloneContentById(id: string): Promise<SubtopicContent | null> {
    const content = await this.client.fetch<SubtopicContent | null>(curriculumStandaloneContent, { id })
    return content ?? null
  }

  /**
   * Fetches grade overview from Sanity (curriculumGradeV2).
   * SubjectOrders are topics; their competencyGoals are subtopics.
   */
  public async getGradeContentFromSanity(gradeId: string): Promise<GradeOverviewFromSanity> {
    const raw: SanityGradeOverviewResponse = await this.client.fetch(curriculumGradeOverview, { id: gradeId })
    const doc = raw?.[0]
    if (!doc) {
      return { gradeId, gradeLevel: 0, title: '', gradeLabel: '', subDivisions: [] }
    }

    const subDivisions = doc.gradeSubdivisions.map((subdivision) => ({
      _id: subdivision._id,
      label: subdivision.label,
      order: subdivision.level,
      topics: this.mapSanityGradeOverviewToTopics(subdivision.subjectOrders),
    }))

    return {
      gradeId: doc._id,
      gradeLevel: doc.gradeLevel,
      title: doc.title ?? '',
      gradeLabel: doc.title ?? '',
      subDivisions: subDivisions.toSorted((a, b) => a.order - b.order),
    }
  }

  public async getSubjectInfoForMappingQuiz(
    gradeId: string,
    subjectOrder: number,
  ): Promise<CurriculumTopicDataType | null> {
    const groqResponse = await this.client.fetch(getSubjectInfoForMappingQuiz, {
      gradeId,
      subjectOrder,
    })
    return groqResponse ?? null
  }

  public async getTopicInfoForMappingQuiz(gradeId: string, topic: number): Promise<CurriculumTopicDataType | null> {
    const groqResponse: { topic: CurriculumTopicDataType } = await this.client.fetch(getTopicInfoForMappingQuiz, {
      gradeId,
      topic,
    })
    return groqResponse?.topic ?? null
  }

  private mapSanityGradeOverviewToTopics(subjectOrders: SanityGradeOverviewSubjectOrder[]): CurriculumTopicDataType[] {
    const sortedSubjectOrders = subjectOrders.toSorted((a, b) => a.order - b.order)

    return sortedSubjectOrders
      .map((so) => {
        const topicId = (so.competencyGoals?.[0]?.topicId ?? 'algebra') as TopicId
        return {
          _id: so._id,
          topicId,
          subjectLabel: so.subjectLabel ?? so.subject ?? '',
          subject: so.subject ?? '',
          subjectOrder: so.order,
          subjectOrderLabel: String(so.order),
          icon: so.icon,
          competencyGoals: (so.competencyGoals ?? [])
            .filter((cg) => (cg.levelCount ?? 0) > 0)
            .map((cg) => ({
              ...cg,
              level: Number(cg.level),
              completeCount: 0,
              levels: [],
            })),
        }
      })
      .filter((topic) => topic.competencyGoals.length > 0)
  }
}

const curriculumSanity = new CurriculumSanity()

export default curriculumSanity
